import {
  EndOrderDetails,
  PaymentMethod,
  TransferDetails,
  isPaymentMethodTransfer,
} from '@pedix-workspace/utils';
import { isOrderPaid } from './order-item.utils';

export type ResolvedTransferSource = {
  source: 'digital' | 'manual';
  cvu: string | null;
  alias: string | null;
  accountHolder: string | null;
};

/**
 * Datos de transferencia manual del comercio (`paymentMethod.transferDetails`), la fuente que cae
 * cuando no hay `digitalPayment` utilizable. Compartida con `resolveTransferSource`,
 * que arma la otra mitad del criterio (precedencia con `digitalPayment` según status/tipo).
 */
export function getManualTransferDetails(
  paymentMethod: PaymentMethod | undefined | null,
): TransferDetails | undefined {
  if (paymentMethod && isPaymentMethodTransfer(paymentMethod)) {
    return paymentMethod.transferDetails;
  }
  return undefined;
}

/**
 * Pago digital liquidado (nada que cobrar). Única definición del corte PAID/REFUNDED: la usan el
 * resolver de transferencias, las instrucciones de pago y la vista de estado (paymentLink).
 */
export function isDigitalPaymentSettled(dp: EndOrderDetails['digitalPayment']): boolean {
  return dp?.status === 'PAID' || dp?.status === 'REFUNDED';
}

export function isOrderPaymentSettled(order: EndOrderDetails): boolean {
  return isDigitalPaymentSettled(order.digitalPayment) || isOrderPaid(order);
}

/**
 * Única fuente de verdad de QUÉ datos de transferencia aplican a un pedido, leyendo las dos
 * fuentes del pago con esta precedencia: `digitalPayment` (integración) manda salvo que esté
 * liquidado (PAID/REFUNDED → no hay nada que cobrar) o CANCELLED/sin datos utilizables, en cuyo
 * caso cae a la transferencia manual del comercio (`paymentMethod.transferDetails`). En ambas
 * fuentes se exige `cvu || alias`: sin dato copiable no hay transferencia que mostrar.
 * La consumen el texto de instrucciones, los mensajes copiables y la vista de estado del pedido —
 * cualquier cambio de precedencia se hace SOLO acá.
 */
export function resolveTransferSource(order: EndOrderDetails): ResolvedTransferSource | null {
  if (isOrderPaymentSettled(order) || order.status === 'canceled') {
    return null;
  }
  const dp = order.digitalPayment;
  if (dp) {
    if (dp.status !== 'CANCELLED' && dp.type === 'transfer' && (dp.cvu || dp.alias)) {
      return {
        source: 'digital',
        cvu: dp.cvu ?? null,
        alias: dp.alias ?? null,
        accountHolder: dp.accountHolder ?? null,
      };
    }
  }

  const manualTransferDetails = getManualTransferDetails(order.paymentMethod);
  if (manualTransferDetails) {
    const { cvu, alias, accountHolder } = manualTransferDetails;
    if (cvu || alias) {
      return {
        source: 'manual',
        cvu: cvu ?? null,
        alias: alias ?? null,
        accountHolder: accountHolder ?? null,
      };
    }
  }

  return null;
}
